/**
 * What the vendor panel draws, composed out of two things the API said and
 * nothing else.
 *
 * This is the vendor's `town-view.ts`: the caller the panel's contract keeps
 * talking about, and therefore the module that has to be right.
 *
 * ## Three prices, one source
 *
 * The **ask** is `GET /api/characters/:id/vendor`'s: each offer arrives already
 * priced, carried through untouched. The **bid** and the **mend** are
 * `GET /api/characters/:id/vendor/quote`'s: every carried thing, priced by the
 * same process that will charge for it, off the state that process holds.
 *
 * **This module used to call `@d2/core`.** Its two pricing functions — what a
 * vendor pays, and what a mend costs — were invoked right here, which was one
 * definition shared rather than a second copy of a rule, but the number a player
 * read was still worked out on a different machine from the one that would charge
 * them, from a snapshot decoded a moment earlier, with a copy of the rules that
 * shipped in a bundle. Nothing forced the two to agree. The quote route removed
 * the last rule from this client entirely: grep this package for either
 * function's name, for a multiplier or for `/ 1024`, and there is nothing.
 *
 * ## It is still a quote, and a quote can go stale
 *
 * A figure on a row is what an exchange **would** come to, read at the moment
 * the counter was drawn. What an exchange **did** come to is the API's own
 * `spent` or `received`, off the answer, and that is what every sentence a player
 * reads afterwards quotes (`vendor-model.ts`). If the two ever disagree — another
 * tab sold something, a run flushed between the read and the press — the charge
 * is the one that happened and the one that is said out loud, and the fresh quote
 * arrives on the very same answer, so the rows correct themselves in the same
 * render that reports the charge. There is no window in which this panel can show
 * one figure and report another without saying so.
 *
 * ## The two decisions left, and they are about containers and rows
 *
 * **The stash is quoted and not offered.** The API will sell out of it — the
 * quote says so, with a real bid on a row marked `stash` — and this panel offers
 * only the pack. That is a decision rather than an omission: a stash a player can
 * sell out of, but cannot see the shape of, sort, or move anything into, is a
 * one-way door, and there is no route between the two containers yet nor a
 * settled answer to what a stash even is (`TODO.md` #25).
 *
 * **A row exists when the vendor quoted a figure for it.** Worn gear carries no
 * bid, because the vendor will not buy what is on your back; a whole item and a
 * ring carry no mend, because there is nothing to charge for. Both absences are
 * the *server's* refusals, stated as `null` before a player can press anything —
 * so the panel still declines to draw a control whose only outcome is a refusal
 * (`skill-tree-model.ts`'s rule) without holding a second opinion about when one
 * would happen.
 *
 * The belt is not quoted. The vendor cannot reach it at all.
 */

import { type QuotedThing, type VendorWindowOffer } from '../account/api';
import { type OfferRow, type RepairRow, type SaleRow, type VendorView } from './vendor-model';

/** The one container this panel declines to put on the counter. See the header. */
const offered = (row: QuotedThing): boolean => row.in !== 'stash';

const offerRow = (offer: VendorWindowOffer): OfferRow => ({
  offerId: offer.offerId,
  name: offer.name,
  // The API's number, whole. See the header.
  price: offer.price,
});

/**
 * A sale row, or none.
 *
 * `flatMap` rather than a filter and a map, so the row is built where `sale` has
 * already been narrowed to a number — there is no branch in which a bid could be
 * defaulted, because there is no expression here that names one.
 */
const saleRow = (row: QuotedThing): readonly SaleRow[] =>
  !offered(row) || row.sale === null ? [] : [{ itemId: row.itemId, name: row.name, bid: row.sale }];

const repairRow = (row: QuotedThing): readonly RepairRow[] => {
  // `durability` is never absent when a mend was quoted — a thing that wears is
  // what a mend is priced from — and the check is here rather than assumed
  // because the alternative to dropping the row would be drawing an invented
  // wear state beside a real price.
  if (!offered(row) || row.repair === null || row.durability === null) return [];

  return [
    { itemId: row.itemId, name: row.name, cost: row.repair, worn: row.in === 'equipment', durability: row.durability },
  ];
};

/**
 * The whole panel, from the window the API sent and the quote it gave.
 *
 * The purse is deliberately absent — from the result and from the arguments.
 * Town already draws one, off the roster row, and a second copy on the same
 * screen is a second thing to be wrong: after an exchange the two would be
 * updated from the same answer at the same moment, right up until the day one of
 * them was not. It is also what makes *"this panel does not decide what a player
 * can afford"* a property of the signature rather than a promise in a comment.
 *
 * The quote arrives in the vendor's own look order — worn, then the pack, then
 * the stash (`vendor-quote.ts`) — and both lists below keep it.
 */
export const vendorViewOf = (
  stock: readonly VendorWindowOffer[],
  quote: readonly QuotedThing[],
): VendorView => ({
  stock: stock.map(offerRow),
  sellable: quote.flatMap(saleRow),
  damaged: quote.flatMap(repairRow),
});
